/**
 * Convenience helpers for accessing UDP field metadata and step schemas.
 *
 * Mirrors the dating-schema field-helpers.ts pattern:
 * pre-built lookup maps with getFieldOptions() and getFieldMeta().
 *
 * @module udp-schema/field-helpers
 */

import type { z } from 'zod';
import { allFieldGroups } from './profile-fields/index.js';
import type { FieldOption, ProfileField } from './profile-fields/types.js';
import { step01ProfileSchema } from './step-01.schema.js';
import { step02PoliticalSchema } from './step-02.schema.js';
import { step03ElectoralSchema } from './step-03.schema.js';
import { step04LeadershipSchema } from './step-04.schema.js';
import { step05PsychologicalSchema } from './step-05.schema.js';
import { step06EducationSchema } from './step-06.schema.js';
import { step07EmploymentSchema } from './step-07.schema.js';
import { step08NeurodiversitySchema } from './step-08.schema.js';
import { step09HealthSchema } from './step-09.schema.js';
import { step10WorldViewSchema } from './step-10.schema.js';
import { step11ResidenceSchema } from './step-11.schema.js';
import { step12HouseholdSchema } from './step-12.schema.js';

// ─── Pre-built lookup maps (like dating-schema) ────────────────────────────

const optionsMap = new Map<string, readonly FieldOption[]>();
const fieldMap = new Map<string, ProfileField>();

for (const group of allFieldGroups) {
  for (const field of group.fields) {
    fieldMap.set(field.key, field);
    if ('options' in field && Array.isArray(field.options)) {
      optionsMap.set(field.key, field.options);
    }
  }
}

// ─── Field metadata helpers (like dating-schema) ────────────────────────────

/**
 * Get the `{ value, label, description }` options array for a field by its key.
 *
 * @example
 * ```ts
 * import { getFieldOptions } from 'udp-schema';
 * const opts = getFieldOptions('gender');
 * // → [{ value: 'man', label: 'Man' }, { value: 'woman', label: 'Woman' }, ...]
 * ```
 */
export function getFieldOptions(fieldKey: string): readonly FieldOption[] {
  return optionsMap.get(fieldKey) ?? [];
}

/**
 * Get full field metadata (label, description, step, type, options, etc.) by key.
 *
 * @example
 * ```ts
 * import { getFieldMeta } from 'udp-schema';
 * const meta = getFieldMeta('familyReligion');
 * // → { key: 'familyReligion', label: 'Family Religion', type: 'single_select', step: 1, options: [...], ... }
 * ```
 */
export function getFieldMeta(fieldKey: string): ProfileField | undefined {
  return fieldMap.get(fieldKey);
}

// ─── Step Schema Registry ───────────────────────────────────────────────────

// biome-ignore lint/suspicious/noExplicitAny: Zod schema types vary per step
const STEP_SCHEMAS: Record<number, z.ZodObject<any>> = {
  1: step01ProfileSchema,
  2: step02PoliticalSchema,
  3: step03ElectoralSchema,
  4: step04LeadershipSchema,
  5: step05PsychologicalSchema,
  6: step06EducationSchema,
  7: step07EmploymentSchema,
  8: step08NeurodiversitySchema,
  9: step09HealthSchema,
  10: step10WorldViewSchema,
  11: step11ResidenceSchema,
  12: step12HouseholdSchema,
};

/**
 * Get the Zod schema for a given step number.
 * Use `.partial().parse(data)` for sub-step saves.
 */
// biome-ignore lint/suspicious/noExplicitAny: Zod schema types vary per step
export function getStepSchema(stepNumber: number): z.ZodObject<any> | undefined {
  return STEP_SCHEMAS[stepNumber];
}

/**
 * Get the list of field keys that are immutable after volunteer approval.
 */
export function getImmutableFields(): readonly string[] {
  return ['firstName', 'middleName', 'lastName', 'gender', 'dateOfBirth'];
}
